import { useState, useEffect } from "react";
import AppLayout from "@/components/feature/AppLayout";
import Button from "@/components/base/Button";
import Modal from "@/components/base/Modal";
import Input from "@/components/base/Input";
import ConfirmDialog from "@/components/feature/ConfirmDialog";
import EmptyState from "@/components/feature/EmptyState";
import { useToast } from "@/store/ToastContext";
import { formatCurrency } from "@/utils/currency";
import { CATEGORY_COLOR_PALETTE, CATEGORY_ICON_OPTIONS } from "@/mocks/categories";
import type { Category } from "@/models/types";
import { categoryApi, getCategoryTotals } from "@/services/api";

export default function Categories() {
  const { showToast } = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [totals, setTotals] = useState<{ category: Category; total: number }[]>([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState(CATEGORY_ICON_OPTIONS[0]);
  const [color, setColor] = useState(CATEGORY_COLOR_PALETTE[0]);
  const [toDelete, setToDelete] = useState<Category | null>(null);
  const [saving, setSaving] = useState(false);

  // Cargar categorías y totales al montar
  useEffect(() => {
    const loadData = async () => {
      try {
        const [cats, totalsData] = await Promise.all([
          categoryApi.list(),
          getCategoryTotals(),
        ]);
        setCategories(cats);
        setTotals(totalsData);
      } catch (error) {
        showToast("Error al cargar datos", "error");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showToast]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setIcon(CATEGORY_ICON_OPTIONS[0]);
    setColor(CATEGORY_COLOR_PALETTE[0]);
    setModalOpen(true);
  };

  const openEdit = (cat: Category) => {
    setEditing(cat);
    setName(cat.name);
    setIcon(cat.icon);
    setColor(cat.color);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showToast("El nombre es obligatorio.", "error");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const updated = await categoryApi.update(editing.id, {
          name: name.trim(),
          icon,
          color,
        });
        setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
        showToast("Categoría actualizada correctamente.");
      } else {
        const created = await categoryApi.create({
          name: name.trim(),
          icon,
          color,
        });
        setCategories((prev) => [...prev, created]);
        showToast("Categoría creada correctamente.");
      }
      // Actualizar totales
      const newTotals = await getCategoryTotals();
      setTotals(newTotals);
      setModalOpen(false);
    } catch (error) {
      showToast("Error al guardar", "error");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

 const handleDelete = async () => {
  if (!toDelete) return;
  try {
    await categoryApi.remove(toDelete.id);
    setCategories((prev) => prev.filter((c) => c.id !== toDelete.id));
    setToDelete(null);
    showToast("Categoría eliminada correctamente.");

    // Actualizar totales sin bloquear ni mostrar errores
    getCategoryTotals()
      .then((newTotals) => setTotals(newTotals))
      .catch((err) => console.error("No se pudieron actualizar los totales", err));
  } catch (error) {
    showToast("Error al eliminar", "error");
    console.error(error);
  }
};

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-foreground-500">
          Administra las categorías de tus gastos.
        </p>
        <Button icon="ri-add-line" onClick={openCreate}>
          Nueva categoría
        </Button>
      </div>

      {categories.length === 0 ? (
        <div className="bg-background-50 border border-background-200 rounded-lg">
          <EmptyState
            icon="ri-price-tag-3-line"
            title="Sin categorías"
            description="Crea categorías para organizar mejor tus gastos."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {totals.map(({ category, total }) => (
            <div
              key={category.id}
              className="bg-background-50 border border-background-200 rounded-lg p-4"
            >
              <div className="flex items-center gap-3">
                <span
                  className="w-11 h-11 rounded-lg flex items-center justify-center text-xl"
                  style={{ backgroundColor: `${category.color}1f`, color: category.color }}
                >
                  <i className={category.icon} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground-950 truncate">
                    {category.name}
                  </p>
                  <p className="text-sm text-foreground-500">
                    {formatCurrency(total, "USD")} {/* Ajusta la moneda según configuración */}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-background-200">
                <button
                  onClick={() => openEdit(category)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-secondary-200 text-foreground-600 cursor-pointer"
                  title="Editar"
                >
                  <i className="ri-edit-line" />
                </button>
                <button
                  onClick={() => setToDelete(category)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-100 text-red-500 cursor-pointer"
                  title="Eliminar"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal crear/editar */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Editar categoría" : "Nueva categoría"}
      >
        <div className="p-5 space-y-5">
          <Input
            label="Nombre"
            name="categoryName"
            placeholder="Ej. Mascotas"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              Icono
            </label>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_ICON_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setIcon(opt)}
                  className={`w-10 h-10 rounded-md flex items-center justify-center text-lg transition-colors cursor-pointer ${
                    icon === opt
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  <i className={opt} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_COLOR_PALETTE.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full cursor-pointer transition-transform ${
                    color === c ? "ring-2 ring-offset-2 ring-foreground-400 scale-110" : ""
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!toDelete}
        title="Eliminar categoría"
        message={`¿Eliminar la categoría "${toDelete?.name}"? Los gastos asociados quedarán sin categoría.`}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      />
    </AppLayout>
  );
}